/**
 * GTM Analytics DataLayer Events
 * The Office of Us Theme
 *
 * Events: view_item_list, select_item, view_item, add_to_cart, remove_from_cart, view_cart
 *
 * Product data sourced from window.__gtm_products (populated by analytics-gtm.liquid).
 * List context persisted via sessionStorage for funnel continuity.
 */

const GTMAnalytics = {
  currency: window.Shopify?.currency?.active || 'USD',

  pushEvent(eventName, ecommerceData) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: eventName, ecommerce: ecommerceData });

    if (window.location.search.includes('gtm_debug=true')) {
      console.log(`[GTM] ${eventName}:`, ecommerceData);
    }
  },

  formatItem(product, index = 0, listName = '') {
    const listId = listName ? listName.toLowerCase().replace(/\s+/g, '_') : '';
    return {
      item_id: String(product.id || product.product_id || ''),
      item_name: product.title || product.product_title || '',
      item_brand: product.vendor || '',
      item_category: product.type || product.product_type || '',
      item_variant: product.variant_title || product.variant || '',
      item_list_name: listName,
      item_list_id: listId,
      index: index + 1,
      price: parseFloat(product.price) / 100 || 0,
      quantity: product.quantity || 1
    };
  },

  // -- List context persistence --

  storeListContext(name) {
    try {
      sessionStorage.setItem('gtm_list', name);
    } catch (e) { /* private browsing */ }
  },

  getListContext() {
    try {
      return sessionStorage.getItem('gtm_list') || '';
    } catch (e) {
      return '';
    }
  },

  // -- Events --

  viewItemList(products, listName) {
    if (!products?.length) return;
    this.storeListContext(listName);

    const listId = listName.toLowerCase().replace(/\s+/g, '_');
    const items = products.map((p, i) => this.formatItem(p, i, listName));

    this.pushEvent('view_item_list', {
      item_list_id: listId,
      item_list_name: listName,
      items
    });
  },

  selectItem(product, listName, index = 0) {
    this.storeListContext(listName);
    const item = this.formatItem(product, index, listName);

    this.pushEvent('select_item', {
      item_list_id: item.item_list_id,
      item_list_name: listName,
      items: [item]
    });
  },

  viewItem(product) {
    const listName = this.getListContext();
    const item = this.formatItem(product, 0, listName);

    this.pushEvent('view_item', {
      currency: this.currency,
      value: item.price,
      items: [item]
    });
  },

  addToCart(product, quantity = 1) {
    const listName = this.getListContext();
    const item = this.formatItem({ ...product, quantity }, 0, listName);

    this.pushEvent('add_to_cart', {
      currency: this.currency,
      value: item.price * quantity,
      items: [item]
    });
  },

  removeFromCart(product, quantity = 1) {
    const listName = this.getListContext();
    const item = this.formatItem({ ...product, quantity }, 0, listName);

    this.pushEvent('remove_from_cart', {
      currency: this.currency,
      value: item.price * quantity,
      items: [item]
    });
  },

  viewCart(cart) {
    if (!cart?.items) return;
    const listName = this.getListContext();
    const listId = listName ? listName.toLowerCase().replace(/\s+/g, '_') : '';

    const items = cart.items.map((item, i) => ({
      item_id: String(item.product_id || ''),
      item_name: item.product_title || '',
      item_brand: item.vendor || '',
      item_category: item.product_type || '',
      item_variant: item.variant_title || '',
      item_list_name: listName,
      item_list_id: listId,
      index: i + 1,
      price: parseFloat(item.price) / 100 || 0,
      quantity: item.quantity || 1
    }));

    this.pushEvent('view_cart', {
      currency: this.currency,
      value: parseFloat(cart.total_price) / 100 || 0,
      items
    });
  },

  // -- Cart state tracking --

  previousCartItems: [],

  async fetchAndStoreCartState() {
    try {
      const res = await fetch('/cart.js');
      this.previousCartItems = (await res.json()).items || [];
    } catch (e) {
      this.previousCartItems = [];
    }
  },

  async onCartDrawerOpen() {
    try {
      const res = await fetch('/cart.js');
      this.viewCart(await res.json());
    } catch (e) { /* silent */ }
  },

  detectRemovedItems(newCart) {
    const newMap = new Map();
    (newCart.items || []).forEach(i => newMap.set(i.variant_id, i.quantity));

    (this.previousCartItems || []).forEach(prev => {
      const newQty = newMap.get(prev.variant_id) || 0;
      if (newQty < prev.quantity) {
        this.removeFromCart({
          id: prev.product_id,
          title: prev.product_title,
          vendor: prev.vendor,
          type: prev.product_type,
          variant_title: prev.variant_title,
          price: prev.price
        }, prev.quantity - newQty);
      }
    });
  },

  // -- Fetch intercept --

  interceptCartFetch() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = async function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      const response = await originalFetch.apply(this, args);

      if (url.includes('/cart/add')) {
        try {
          const data = await response.clone().json();
          const items = data.items || (data.product_id ? [data] : []);
          items.forEach(item => {
            self.addToCart({
              id: item.product_id,
              title: item.product_title,
              vendor: item.vendor,
              type: item.product_type,
              variant_title: item.variant_title,
              price: item.price
            }, item.quantity || 1);
          });
          self.fetchAndStoreCartState();
        } catch (e) { /* non-JSON response */ }
      }

      if (url.includes('/cart/change')) {
        try {
          const newCart = await response.clone().json();
          self.detectRemovedItems(newCart);
          self.previousCartItems = newCart.items || [];
        } catch (e) { /* silent */ }
      }

      return response;
    };
  },

  // -- Product card click --

  getListNameFromContext(card) {
    const collection = document.querySelector('[data-collection-title]');
    if (collection) return collection.dataset.collectionTitle || 'Collection';

    if (window.location.pathname.includes('/search')) {
      return 'Search Results: ' + (new URLSearchParams(window.location.search).get('q') || '');
    }

    if (card.closest('product-recommendations')) return 'Product Recommendations';

    const section = card.closest('[data-section-type]');
    if (section) return section.dataset.sectionType || 'Featured Products';

    return 'Product List';
  },

  getProductIndex(card) {
    const container = card.closest('.product-grid, .resource-list, .collection-wrapper');
    if (!container) return 0;
    return Array.from(container.querySelectorAll('product-card, .product-card')).indexOf(card);
  },

  // -- Init --

  init() {
    this.interceptCartFetch();
    this.fetchAndStoreCartState();

    document.addEventListener('dialog:open', (e) => {
      if (e.target?.closest?.('cart-drawer-component')) this.onCartDrawerOpen();
    });

    document.addEventListener('click', (e) => {
      const card = e.target?.closest?.('product-card, .product-card');
      if (!card || e.target.closest('.quick-add-toous-button, .quick-add__button')) return;

      const id = card.dataset.productId;
      const product = (window.__gtm_products || {})[id];
      if (!product) return;

      const listName = this.getListNameFromContext(card);
      this.selectItem(product, listName, this.getProductIndex(card));
    });
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => GTMAnalytics.init());
} else {
  GTMAnalytics.init();
}

window.GTMAnalytics = GTMAnalytics;
